<template>
  <div>
  <form class="form" @submit="submitForm" autocomplete="off" v-if="!isEditAddressMode">
    <input type="hidden" autocomplete="false" />
    <!-- first_name -->
    <div class="form-group">
      <label class="form-label">First Name *</label>
      <input v-model="formData.first_name" @focusout="validateFirstName()" class="form-control">
      <span v-if="errors.first_name" class="form-error">First name is required</span>
    </div>
    <!-- last_name -->
    <div class="form-group">
      <label class="form-label">Last Name *</label>
      <input v-model="formData.last_name" @focusout="validateLastName()" class="form-control">
      <span v-if="errors.last_name" class="form-error">Last name is required</span>
    </div>
    <!-- customer nickname -->
    <div class="form-group">
      <label class="form-label">Nickname</label>
      <input v-model="formData.nickname" class="form-control">
    </div>
    <!-- email -->
    <div v-if="customerAddresses">
      <div class="form-group">
        <label class="form-label">Email *</label>
        <input v-model="formData.email" @focusout="validateEmail()" class="form-control">
        <span v-if="errors.email" class="form-error">Email is required</span>
      </div>
    </div>
    <div class="form-group" v-if="$attrs['show-tier']">
      <label class="form-label">Tier *</label>
      <div>
        <button type="button" class="btn tab-toggle tab-toggle-tier" :class="{ active: formData.tier === 1}" @click="formData.tier === 1 ? formData.tier = null : formData.tier = 1">
          Tier 1
        </button>
        <button type="button" class="btn tab-toggle tab-toggle-tier" :class="{ active: formData.tier === 2}" @click="formData.tier === 2 ? formData.tier = null : formData.tier = 2">
          Tier 2
        </button>
        <button type="button" class="btn tab-toggle tab-toggle-tier" :class="{ active: formData.tier === 3}" @click="formData.tier === 3 ? formData.tier = null : formData.tier = 3">
          Tier 3
        </button>
      </div>
      <span v-if="errors.tier" class="form-error">Tier is required</span>
    </div>
    <div class="form-group" v-if="$attrs['show-notification']">
      <legend class="form-label">Type</legend>
      <div class="form-group form-radio-group types-radio-group">
        <div class="grid-col type-container" v-for="(type, index) in types" :key="type.id" :data-id="type.id" :data-counter="index" v-bind:style="{ 'background-color': type?.type_color?.hex_value || '#FFF' }">
          <label class="form-radio-button">
          <input
            @click="handleTypeClick"
            :value="type.id"
            :checked="formData.customer_types.map((customerType) => customerType).includes(type.id)"
            type="checkbox">{{ type.name }}
          </label>
          <div v-if="formData.customer_types.map((customerType) => customerType).includes(type.id) || formData.customer_sub_types.map((customerSubType) => customerSubType.type_id).includes(type.id)" class="sub-type-container">
            <div v-for="(sub_type) in type.sub_types" :key="sub_type.id" v-bind:style="{ 'background-color': sub_type?.sub_type_color?.rgba_value || '#FFF' }">
              <label class="form-radio-button">
                <input
                  @click="handleSubTypeClick($event, sub_type, type)"
                  :value="sub_type.id"
                  :checked="formData.customer_sub_types.map((customerSubType) => customerSubType.id).includes(sub_type.id)"
                  type="checkbox">> {{ sub_type.name }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- instagram_handle -->
    <div class="form-group">
      <label class="form-label">Instagram
        <div class="input-group">
              <span class="input-group__icon">
                @
              </span>
        <input v-model="formData.instagram_handle" class="form-control" placeholder="example_1234">
        </div>
      </label>
    </div>
    <!-- phone (must be uniq + in correct international format, otherwise server can throw validation error) -->
    <div class="form-group">
      <label class="form-label">Phone *
        <input v-model="formData.phone" type="tel" @focusout="validatePhone()" class="form-control">
        <span v-if="errors.phone" class="form-error">Phone is required</span>
      </label>
    </div>
    <div v-if="!customerAddresses">
      <!-- address nickname -->
      <div class="form-group">
        <label class="form-label">Address Nickname</label>
        <input v-model="formData.address_nickname" class="form-control">
      </div>
      <!-- address1 -->
      <div class="form-group">
        <label class="form-label">Address 1 *
          <input @focusout="validateAddress1()" @input="validateAddress1()" v-model="formData.address1" class="form-control">
        </label>
        <span v-if="errors.address1 === 'required'" class="form-error">Address is required</span>
        <span v-if="errors.address1 === 'length'" class="form-error">Address must be 30 characters or less</span>
      </div>
      <!-- address2 -->
      <div class="form-group">
        <label class="form-label">Address 2
          <input @focusout="validateAddress2()" @input="validateAddress2()" v-model="formData.address2" class="form-control">
        </label>
        <span v-if="errors.address2" class="form-error">Address must be 30 characters or less</span>
      </div>
      <!-- city -->
      <div class="form-group">
        <label class="form-label">City *
          <input @focusout="validateCity()" v-model="formData.city" class="form-control">
        </label>
        <span v-if="errors.city" class="form-error">City is required</span>
      </div>
      <!-- province -->
      <div class="form-group">
        <label class="form-label">State *
          <select @change="validateState()" v-if="countryProvinces.length" class="form-control" v-model="formData.province">
            <option value=""/>
            <option :key="index" :value="provinceName" v-for="(provinceName, index) in countryProvinces">{{
                provinceName
              }}
            </option>
          </select>
          <input v-else v-model="formData.province" class="form-control">
        </label>
        <span v-if="errors.province" class="form-error">State is required</span>
      </div>
      <!-- zip -->
      <div class="form-group">
        <label class="form-label">Zip Code *
          <input @focusout="validateZip()" v-model="formData.zip" class="form-control">
        </label>
        <span v-if="errors.zip" class="form-error">Zip is required</span>
      </div>
      <!-- country -->
      <div class="form-group" v-if="countries.length">
        <label class="form-label">Country *
          <select class="form-control" v-model="formData.country" @change="switchProvince($event.target.value); onCountryChange()">
            <option value=""/>
            <option :key="c.id" :value="c.name" v-for="c in countries">{{ c.name }}</option>
          </select>
        </label>
      </div>
    </div>
    <div v-else>
      <div class="form-group">
        <label class="form-label">Saved Addresses</label>
      </div>
      <CustomerAddress
        :address="address"
        :customer-id="customerId"
        :customer-addresses-count="customerAddresses.length"
        @customerEditAddress="updateCustomerAddress"
        @customerDeleteAddress="updateCustomerAddresses"
        @customerCannotDeleteAddress="showDeleteAddressError"
        v-for="address in customerAddresses" :key="address.id"/>
        <div class="form-group">
          <div class="input-group">
              <span class="input-group__icon">
                +
              </span>
            <button type="button" class="form-control" @click="updateCustomerAddress">Add New Address</button>
          </div>
          <span v-if="errors.address" class="form-error">Address is required</span>
        </div>

    </div>
    <!-- notification preference -->
    <div class="form-group" v-if="$attrs['show-notification']">
      <legend class="form-label">Notification Preference</legend>
      <div class="form-group form-radio-group">
        <label class="form-radio-button">
          <input v-model="formData.notification_preference"
                 :value="dictionaries.notificationPreferences.all.value"
                 type="radio">{{ dictionaries.notificationPreferences.all.label }}</label>
        <label class="form-radio-button">
          <input v-model="formData.notification_preference"
                 :value="dictionaries.notificationPreferences.shipping.value"
                 type="radio">{{ dictionaries.notificationPreferences.shipping.label }}</label>
        <label class="form-radio-button">
          <input v-model="formData.notification_preference"
                 :value="dictionaries.notificationPreferences.none.value"
                 type="radio">{{ dictionaries.notificationPreferences.none.label }}</label>
      </div>
    </div>
    <div class="backend-error-information" v-if="errorInfo">
      <p>Please adjust following:</p>
      {{errorInfo}}
    </div>
    <div class="buttons">
      <button type="submit" name="submit" class="btn btn--primary">
        {{ $attrs['button-text'] }}
      </button>
      <p class="form-error center">{{ $attrs['error-message']  }}</p>
    </div>
  </form>
  <div class="full-page" v-if="isEditAddressMode">
    <div class="full-page-confirmation" v-if="showSuccessMsg">Address saved successfully!</div>
  <form class="form container content" @submit="submitAddressForm" autocomplete="off" v-if="!showSuccessMsg">
    <div class="panel__heading">
      <h2 v-if="formAddressData.address1 == undefined">Add new address</h2>
      <h2 v-if="formAddressData.address1 != undefined">Edit address</h2>
    </div>

    <!-- address nickname -->
    <div class="form-group">
      <label class="form-label">Address Nickname</label>
      <input v-model="formAddressData.name" class="form-control">
    </div>

    <!-- address1 -->
    <div class="form-group">
      <label class="form-label">Address 1 *
        <input v-model="formAddressData.address1" class="form-control">
      </label>
      <span v-if="validateEditAddress1()" class="form-error">Address must be 30 characters or less</span>
    </div>
    <!-- address2 -->
    <div class="form-group">
      <label class="form-label">Address 2
        <input v-model="formAddressData.address2" class="form-control">
      </label>
      <span v-if="validateEditAddress2()" class="form-error">Address must be 30 characters or less</span>
    </div>
    <!-- city -->
    <div class="form-group">
      <label class="form-label">City *
        <input v-model="formAddressData.city" class="form-control">
      </label>
    </div>
    <!-- province -->
    <div class="form-group">
      <label class="form-label">State *
        <select v-if="countryProvinces.length" class="form-control" v-model="formAddressData.state">
          <option value=""/>
          <option :key="index" :value="provinceName" v-for="(provinceName, index) in countryProvinces">{{
              provinceName
            }}
          </option>
        </select>
        <input v-else v-model="formAddressData.state" class="form-control">
      </label>
    </div>
    <!-- zip -->
    <div class="form-group">
      <label class="form-label">Zip Code *
        <input v-model="formAddressData.zip" class="form-control">
      </label>
    </div>
    <!-- country -->
    <div class="form-group" v-if="countries.length">
      <label class="form-label">Country *
        <select class="form-control" v-model="formAddressData.country" @change="switchProvince($event.target.value)">
          <option value=""/>
          <option :key="c.id" :value="c.name" v-for="c in countries">{{ c.name }}</option>
        </select>
      </label>
    </div>
    <!-- defaultAddress -->
    <div class="form-group">
      <div class="form-group form-radio-group">
        <label class="form-radio-button">
          <input
            v-model="formAddressData.default"
            type="checkbox">Default Address
        </label>
      </div>
    </div>
    <div class="backend-error-information" v-if="validationMessage">
      <p>Please adjust following:</p>
      {{validationMessage}}
    </div>

    <div class="buttons">
      <button type="submit" name="submit" class="btn btn--primary" :disabled="isSubmitAddressDisabled()">
        Save
      </button>
    </div>
  </form>
  </div>
  </div>
</template>

<script>

import CustomerAddress from '../components/CustomerAddress.vue';
import api from '../api'
export default {
  components: {
    CustomerAddress
  },
  props: {
    tier: {
      type: Number,
      default: null
    },
    firstName: {
      type: String,
      default: ''
    },
    lastName: {
      type: String,
      default: ''
    },
    customerNickname: {
      type: String,
      default: ''
    },
    instagramHandle: {
      type: String,
      default: null
    },
    email: {
      type: String,
      default: null
    },
    phone: {
      type: String,
      default: ''
    },
    address1: {
      type: String,
      default: ''
    },
    address2: {
      type: String,
      default: null
    },
    city: {
      type: String,
      default: ''
    },
    province: {
      type: String,
      default: ''
    },
    zip: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: ''
    },
    notificationPreference: {
      type: String,
      default: ''
    },
    defaultAddress: {
      type: Boolean,
      default: false
    },
    creatingCustomer: {
      type: Boolean,
      default: false
    },
    customerTypes: {
      type: Array,
      default() {
        return []
      }
    },
    customerSubTypes: {
      type: Array,
      default() {
        return []
      }
    },
    customerAddresses: {
      type: Array,
      default: null
    },
    customerId: {
      type: Number,
      default: null
    },
    errorInfo: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      errors: {
        first_name: false,
        last_name: false,
        phone: false,
        tier: false,
        address: false,
        address1: false,
        address2: false,
        city: false,
        province: false,
        zip: false,
        email: false,
      },
      formData: {
        tier: this.tier,
        first_name: this.firstName,
        last_name: this.lastName,
        nickname: this.customerNickname,
        email: this.email,
        instagram_handle: this.instagramHandle,
        phone: this.phone,
        address1: this.address1,
        address2: this.address2,
        city: this.city,
        province: this.province,
        zip: this.zip,
        country: this.country,
        notification_preference: this.notificationPreference,
        customer_types: [],
        customer_sub_types: [],
        default: true,
        address_nickname: this.address_nickname
      },
      formAddressData:{
        address1: this.address1,
        address2: this.address2,
        city: this.city,
        province: this.province,
        zip: this.zip,
        country: this.country,
        default: this.defaultAddress,
        name: this.name
      },
      countries: [],
      types: [],
      countryProvinces: [],
      isEditAddressMode: false,
      showSuccessMsg: false,
      validationMessage: ''
    }
  },
  emits: ["contactFormSubmitted", "addressFormSubmitted"],
  methods: {
    submitForm(e) {
      if(this.validateFields()) {
        e.preventDefault();
        this.$emit('contactFormSubmitted', this.formData, e.target);
      } else {
        e.preventDefault();
      }
    },
    validateFirstName() {
      if(!this.formData.first_name) {
        this.errors.first_name = true;
      } else {
        this.errors.first_name = false;
      }
    },
    validateLastName() {
      if(!this.formData.last_name) {
        this.errors.last_name = true;
      } else {
        this.errors.last_name = false;
      }
    },
    validateEmail() {
      if(!this.formData.email) {
        this.errors.email = true;
      } else {
        this.errors.email = false;
      }
    },
    validatePhone() {
      if(!this.formData.phone) {
        this.errors.phone = true;
      } else {
        this.errors.phone = false;
      }
    },
    validateTier() {
      if(!this.formData.tier) {
        this.errors.tier = true;
      } else {
        this.errors.tier = false;
      }
    },
    validateAddress1() {
      if(!this.formData.address1) {
        this.errors.address1 = 'required';
      } else if(this.formData.country === 'United States' && this.formData.address1.length > 30) {
        this.errors.address1 = 'length';
      } else {
        this.errors.address1 = false;
      }
    },
    validateAddress2() {
      if(this.formData.country === 'United States' && this.formData.address2 && this.formData.address2.length > 30) {
        this.errors.address2 = true;
      } else {
        this.errors.address2 = false;
      }
    },
    validateEditAddress1() {
      if(this.formAddressData.country === 'United States' && this.formAddressData.address1 && this.formAddressData.address1.length > 30) {
        return true;
      }
      return false;
    },
    validateEditAddress2() {
      if(this.formAddressData.country === 'United States' && this.formAddressData.address2 && this.formAddressData.address2.length > 30) {
        return true;
      }
      return false;
    },
    validateCity() {
      if(!this.formData.city) {
        this.errors.city = true;
      } else {
        this.errors.city = false;
      }
    },
    validateState() {
      if(!this.formData.province || this.formData.province == '') {
        this.errors.province = true;
      } else {
        this.errors.province = false;
      }
    },
    validateZip() {
      if(!this.formData.zip) {
        this.errors.zip = true;
      } else {
        this.errors.zip = false;
      }
    },
    validateFields() {
      this.validateFirstName()
      this.validateLastName()
      this.validatePhone()
      this.validateTier()
      this.validateAddress1()
      this.validateAddress2()
      this.validateCity()
      this.validateState()
      this.validateZip()
      this.validateEmail()
      if(this.creatingCustomer) {
        return (!this.errors.first_name && !this.errors.last_name && !this.errors.phone && !this.errors.tier && !this.errors.address1 && !this.errors.address2 && !this.errors.city && !this.errors.province && !this.errors.zip)
      } else {
        return (!this.errors.first_name && !this.errors.last_name && !this.errors.phone && !this.errors.tier && !this.errors.email)
      }
    },
    switchProvince(countryName, province = '') {
      this.formData.province = province;
      const country = this.countries.find(c => c.name === countryName);
      if (country && country.provinces) {
        this.countryProvinces = country.provinces;
      } else {
        this.countryProvinces.length = 0;
      }
    },
    onCountryChange() {
      this.validateAddress1();
      this.validateAddress2();
    },
    isSubmitDisabled() {
      const mandatoryFields = ['first_name', 'last_name', 'phone'];
      if (this.$attrs['show-tier']) {
        mandatoryFields.push('tier');
      }
      return !mandatoryFields.every(field => (this.formData[field] != null && this.formData[field] !== ''));
    },
    handleSubTypeClick($event, subType, type) {
      if (this.formData.customer_sub_types.filter((subtype) => subtype.type_id == type.id).length == 3 && $event.target.checked) {
        $event.preventDefault()
        return
      }
      if ($event.target.checked) {
        this.formData.customer_sub_types = this.formData.customer_sub_types.concat(subType)
      } else {
        this.formData.customer_sub_types = this.formData.customer_sub_types.filter((el) => el.id != subType.id)
      }
    },
    handleTypeClick(e) {
      if (this.formData.customer_types.length == 3 && e.target.checked) {
        e.preventDefault()
        return
      }
      if (e.target.checked) {
        let type = this.types.find((type) => type.id == parseInt(e.target.value))
        this.formData.customer_types = this.formData.customer_types.concat(type.id)
      } else {
        this.formData.customer_sub_types = this.formData.customer_sub_types.filter((el) => el.type_id != parseInt(e.target.value))
        this.formData.customer_types = this.formData.customer_types.filter((el) => el != e.target.value)
      }
    },
    isSubmitAddressDisabled() {
      const mandatoryFields = ['city', 'state', 'zip', 'address1', 'country'];
      const mandatoryFieldsValid = mandatoryFields.every(field => (this.formAddressData[field] != null && this.formAddressData[field] !== ''));
      const addressLengthValid = !this.validateEditAddress1() && !this.validateEditAddress2();
      return !mandatoryFieldsValid || !addressLengthValid;
    },
    showDeleteAddressError() {
      this.errors.address = true
    },
    updateCustomerAddress(address) {
      this.isEditAddressMode = true;
      history.pushState({}, '', window.location.href + '/editAddress');

      const country = address.country || 'United States'

      const countryObj = this.countries.find(c => c.name === country);
      if (countryObj && countryObj.provinces) {
        this.countryProvinces = countryObj.provinces;
      } else {
        this.countryProvinces.length = 0;
      }

      this.formAddressData = {
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: country,
        default: address.default,
        name: address.name,
        customer_id: address.customer_id,
        shopify_id: address.shopify_id
      }
    },
    updateCustomerAddresses(customerId) {
      this.$emit('addressFormSubmitted', customerId);
    },
    handleAddressError(e) {
      if (e.status === 400 || e.data) {
        // ApiError instance
        this.validationMessage = e.data;
      } else if (e.response?.status === 400) {
        // Raw Axios error
        this.validationMessage = e.response.data.errors;
      } else {
        console.error(e);
        this.isEditAddressMode = false;
      }
    },
    submitAddressForm(e) {
      e.preventDefault();
      if (!this.formAddressData.customer_id) {
        api.createCustomerAddress(this.formAddressData, this.customerId).then(() => {
          this.validationMessage = '';
          this.showSuccessMsg = true;
          this.$emit('addressFormSubmitted', this.customerId);
          setTimeout(()=>{
            this.showSuccessMsg = false;
            this.isEditAddressMode = false;
            history.go(-1);
          }, 2000);
        }).catch((err) => {
          this.handleAddressError(err);
        })
      } else {
        api.updateCustomerAddress(this.formAddressData, this.customerId).then(() => {
          this.validationMessage = '';
          this.showSuccessMsg = true;
          this.$emit('addressFormSubmitted', this.customerId);
          setTimeout(()=>{
            this.showSuccessMsg = false;
            this.isEditAddressMode = false
            history.go(-1);
          }, 3000);
        }).catch((err) => {
          this.handleAddressError(err);
        })
      }
    },
    handleBackButton(){
      this.isEditAddressMode = false;
    }
  },
  mounted() {
    this.formData.customer_sub_types = this.$props.customerSubTypes
    this.formData.customer_types = this.$props.customerTypes
    window.addEventListener("popstate", this.handleBackButton, false);
    this.axios.get('/v1/countries.json')
      .then(response => {
        this.countries = response.data;
        if (this.formData.country) {
          this.switchProvince(this.formData.country, this.formData.province);
        }
      })
      .catch((error) => {
        console.log(error)
      });
    this.axios.get('/v1/types.json')
      .then(response => {
        this.types = response.data.records;
      })
      .catch((error) => {
        console.log(error)
      });
  },
  onUnmounted() {
    window.removeEventListener("popstate", this.handleBackButton);
  }
}
</script>
<style scoped>
  .grid-col {
    margin-top: 0;
  }

  .form-error {
    color: red;
  }

  .center {
    text-align: center
  }

  input[type=checkbox]:checked{
    accent-color: black;
  }

  .tab-toggle-tier {
    color: black;
  }

  .full-page {
    position: fixed;
    top: 60px;
    left: 0;
    width: 100%;
    background: #F7F7F7;
  }

  .form.container {
    margin: 0 auto;
  }

  .input-group button {
    text-align: left;
  }

  .types-radio-group {
    border: none;
    background: none;
  }

  .type-container {
    margin-bottom: 5px;
  }
  .sub-type-container {
    background: white;
  }
</style>
